import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useBranding } from '../../lib/BrandingContext';
import { Scale } from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [stats, setStats] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [tenantMonthlyStats, setTenantMonthlyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    // Check for token first
    if (!token) {
      alert('Please login first');
      navigate('/login');
      return;
    }
    // Get user info
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchStats();
  }, [token, navigate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/admin/system/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
      
      // Fetch monthly opinion stats
      const monthlyResponse = await axios.get(`${backendUrl}/api/admin/opinions/monthly-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonthlyStats(monthlyResponse.data);
      
      // Fetch tenant-wise monthly stats
      const tenantMonthlyResponse = await axios.get(`${backendUrl}/api/admin/opinions/tenant-monthly-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenantMonthlyStats(tenantMonthlyResponse.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch statistics');
      if (err.response?.status === 403) {
        alert('You do not have admin access');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (error) return <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with Logo and Name */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-4">
            {branding.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt={branding.company_name || 'Logo'} 
                className="h-12 w-auto"
              />
            ) : (
              <div 
                className="p-3 rounded-lg"
                style={{ backgroundColor: `var(--primary-color, #2563eb)` }}
              >
                <Scale className="w-8 h-8 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-600">{branding.company_name || 'TechOrange-Legal'}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Back to App
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm font-medium">Total Tenants</h3>
            <p className="text-3xl font-bold text-blue-600 mt-2">{stats?.tenants?.total || 0}</p>
            <p className="text-sm text-gray-600 mt-1">{stats?.tenants?.active || 0} active</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm font-medium">Total Users</h3>
            <p className="text-3xl font-bold text-green-600 mt-2">{stats?.users?.total || 0}</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm font-medium">Total Opinions</h3>
            <p className="text-3xl font-bold text-purple-600 mt-2">{stats?.opinions?.total || 0}</p>
          </div>

          <div 
            className="bg-white p-6 rounded-lg shadow cursor-pointer hover:shadow-lg transition"
            onClick={() => navigate('/audit-logs')}
          >
            <h3 className="text-gray-500 text-sm font-medium">Audit Logs</h3>
            <p className="text-3xl font-bold text-blue-600 mt-2">📋</p>
            <p className="text-sm text-blue-600 mt-1 font-medium">View Activity →</p>
          </div>
        </div>

        {/* Monthly Opinion Statistics */}
        {monthlyStats && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Legal Opinions Generated - Monthly Breakdown</h2>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-lg">
                <h3 className="text-lg font-semibold mb-2">Total To Date</h3>
                <p className="text-4xl font-bold">{monthlyStats.total_to_date.toLocaleString()}</p>
                <p className="text-blue-100 text-sm mt-2">All time legal opinions generated</p>
              </div>
              
              <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-lg shadow-lg">
                <h3 className="text-lg font-semibold mb-2">{monthlyStats.current_month.month}</h3>
                <p className="text-4xl font-bold">{monthlyStats.current_month.count.toLocaleString()}</p>
                <p className="text-green-100 text-sm mt-2">Opinions generated this month</p>
              </div>
            </div>

            {/* Monthly Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Month
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Opinions Generated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      % of Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {monthlyStats.monthly_stats.slice().reverse().map((stat, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">{stat.month}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900 font-semibold">{stat.count.toLocaleString()}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-600">
                            {((stat.count / monthlyStats.total_to_date) * 100).toFixed(1)}%
                          </span>
                          <div className="ml-3 w-24 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${(stat.count / monthlyStats.total_to_date) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {monthlyStats.monthly_stats.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No opinion data available yet
              </div>
            )}
          </div>
        )}

        {/* Tenant-Wise Monthly Statistics */}
        {tenantMonthlyStats && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Legal Opinions by Tenant - Monthly Breakdown</h2>
            
            {tenantMonthlyStats.tenant_stats.map((tenant, tenantIndex) => (
              <div key={tenantIndex} className="mb-8 last:mb-0">
                {/* Tenant Header */}
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 rounded-t-lg">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-semibold">{tenant.tenant_name}</h3>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{tenant.total.toLocaleString()}</p>
                      <p className="text-purple-100 text-sm">Total Opinions</p>
                    </div>
                  </div>
                </div>

                {/* Monthly Data Table */}
                <div className="border border-t-0 border-gray-200 rounded-b-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Month
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Opinions Generated
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          % of Tenant Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tenant.monthly_data.map((monthData, monthIndex) => (
                        <tr key={monthIndex} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900">{monthData.month}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900 font-semibold">{monthData.count.toLocaleString()}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-sm text-gray-600">
                                {((monthData.count / tenant.total) * 100).toFixed(1)}%
                              </span>
                              <div className="ml-3 w-24 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-purple-600 h-2 rounded-full"
                                  style={{ width: `${(monthData.count / tenant.total) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {tenantMonthlyStats.tenant_stats.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No tenant opinion data available yet
              </div>
            )}
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <button
            onClick={() => navigate('/admin/tenants')}
            className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition text-left"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Manage Tenants</h3>
            <p className="text-gray-600 text-sm">Create, view, and manage tenants</p>
          </button>

          <button
            onClick={() => navigate('/admin/users')}
            className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition text-left"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Manage Users</h3>
            <p className="text-gray-600 text-sm">View and manage user roles</p>
          </button>

          <button
            onClick={() => navigate('/admin/billing')}
            className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition text-left"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Billing & Usage</h3>
            <p className="text-gray-600 text-sm">View usage and billing information</p>
          </button>

          <button
            onClick={() => navigate('/password-management')}
            className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition text-left"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Password Management</h3>
            <p className="text-gray-600 text-sm">Change password & reset user passwords</p>
          </button>

          <button
            onClick={() => navigate('/admin/branding')}
            className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition text-left"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Branding Settings</h3>
            <p className="text-gray-600 text-sm">Customize white-label branding & themes</p>
          </button>

          <button
            onClick={() => navigate('/admin/domains')}
            className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition text-left"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Domain Management</h3>
            <p className="text-gray-600 text-sm">Configure custom domains & subdomains</p>
          </button>

          <button
            onClick={() => navigate('/audit-logs')}
            className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg shadow-lg hover:shadow-xl transition text-left border-2 border-blue-400"
          >
            <h3 className="text-lg font-bold text-blue-900 mb-2">📋 Audit Logs</h3>
            <p className="text-blue-700 text-sm font-medium">View system activity and user actions</p>
          </button>

          {user?.role === 'super_admin' && (
            <>
              <button
                onClick={() => navigate('/admin/feature-toggles')}
                className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition text-left"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Feature Toggles</h3>
                <p className="text-gray-600 text-sm">Enable/disable dashboard features (Super Admin only)</p>
              </button>
              
              <button
                onClick={() => navigate('/admin/state-management')}
                className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition text-left border-2 border-purple-200"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2">🗺️ State & AI Management</h3>
                <p className="text-gray-600 text-sm">Manage tenant states & AI keys (Super Admin only)</p>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
